import { Button, Col, Input, Pagination, Row, Space, Table, TableColumnsType, Typography, Spin } from "antd";
import type { SortOrder } from "antd/lib/table/interface";
import { Icon } from "@iconify/react";
import { useEffect, useMemo, useState } from "react";
import type { TCreatedPdf } from "pdfmake/build/pdfmake";

import { PDFViewer } from "../../../../Components/PDFViewer/PDFViewer";
import { getFormattedDate, hasKeyString, formatDocVersion, parseDocVersionNumber } from "../../../../Utils/CommonUtils";
import { useNotification } from "../../../../Hooks/useNotification";
import { toArray, workOrderApi } from "../../../../Services";
import { downloadDocument, nextDocumentId, workOrderDocuments } from "../../Constants";
import { EditDocumentModal, EditDocumentSubmitPayload, SavedDocument } from "./EditDocumentModal";
import styles from "../../WorkOrder.module.scss";

interface Props {
  detail: any;
  envelopeId?: string;
  /** The work order PDF exactly as View / Draft Work Order build it. */
  workOrderPdf: (customDetail?: any) => Promise<TCreatedPdf>;
  fileName: string;
  /** Bumped when the work order re-reads, so the version list does too. */
  refreshKey?: number;
  /** Re-reads the work order once an upload lands, so the new version shows. */
  onUploaded?: () => void;
  /** A draft: its versions are read under the "-draft" id (uploads keep the plain one). */
  isDraft?: boolean;
}

/**
 * Detail — Document Versions tab, on e-POD's pattern.
 *
 * Upload: Add sends each picked file to POST /document/panel/upload (envelopeId,
 * documentId counting on from the highest held, draft while the work order is
 * one — no docVersion, the backend assigns it), then re-reads the work order.
 * Retrieval: the versions are GET /document/get/document/versions, ten a page,
 * newest first. `detail` carries the current-version documents
 * (/document/get/documents/current-version): the newest version lists them all,
 * an older one the files still at its version — unless the version row brings
 * its own files. Each opens at its blobUrl plus the SAS key. Version 0 — shown
 * as 1.0 — is the one the work order was created with and also carries its
 * generated PDF.
 */
const PAGE_SIZE = 10;
/* Never back to "unsorted": antd steps through these, so after descend comes ascend. */
const FLIP: SortOrder[] = ["ascend", "descend", "ascend"];
/* Numbered versions 1.0, 1.1, 1.2… converted to V0, V1, V2… matching SPA convention. */
const versionLabel = (value: any): string => formatDocVersion(value);
const isFirstVersion = (value: any) => {
  const label = versionLabel(value);
  return label === "V0" || String(value) === "0" || String(value) === "1.0";
};

export const DocumentVersions = ({
  detail,
  envelopeId,
  workOrderPdf,
  fileName,
  refreshKey,
  onUploaded,
  isDraft = false,
}: Props) => {
  const { openNotification } = useNotification();
  const isDraftStatus =
    isDraft ||
    String(detail?.status ?? "").toUpperCase() === "DRAFT" ||
    /-draft$/i.test(envelopeId ?? "");
  /* A completed work order is closed: no new versions. */
  const isCompleted = String(detail?.status ?? "").toUpperCase() === "COMPLETED";
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState<{ name: string; url: string; revoke?: boolean } | null>(null);
  const [previewFromEdit, setPreviewFromEdit] = useState(false);
  const [isReturningFromPreview, setIsReturningFromPreview] = useState(false);

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [orderBy, setOrderBy] = useState("docVersion");
  const [records, setRecords] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  /* Each version's own files, read when its row is opened (e-POD's pattern),
     keyed by docVersion. Cleared whenever the list re-reads. */
  const [versionFiles, setVersionFiles] = useState<Record<string, any[]>>({});
  const [loadingVersionKeys, setLoadingVersionKeys] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!envelopeId) return;
    if (isDraftStatus) {
      setRecords([]);
      setTotal(0);
      setIsLoading(false);
      setVersionFiles({});
      return;
    }
    setIsLoading(true);
    setVersionFiles({});
    workOrderApi
      .documentVersions({ envelopeId, draft: isDraft, page, size: PAGE_SIZE, order, orderBy })
      .then((result: any) => {
        setRecords(toArray(result));
        setTotal(Number(result?.totalCount) || toArray(result).length);
      })
      .catch(() => {
        setRecords([]);
        setTotal(0);
      })
      .finally(() => setIsLoading(false));
  }, [envelopeId, isDraft, isDraftStatus, page, order, orderBy, refreshKey]);

  const loadVersion = (docVersion: any, order?: any) => {
    const key = String(docVersion);
    if (!envelopeId || versionFiles[key]) return;
    const isVer0 =
      String(docVersion) === "0" ||
      String(docVersion) === "1.0" ||
      order === 0 ||
      isFirstVersion(docVersion);
    if (isDraftStatus || isVer0) return;

    setLoadingVersionKeys((prev) => ({ ...prev, [key]: true }));
    const versionPayload = order !== undefined && order !== null ? order : docVersion;
    workOrderApi
      .versionDocuments(envelopeId, versionPayload, isDraft)
      .then((data: any) => {
        const cleanId = envelopeId.replace(/-draft$/i, "");
        const numV = parseFloat(String(docVersion));
        const defaultPath = `spa-be/${cleanId}/V${numV === 0 || numV === 1 ? 1 : Math.round(numV)}`;
        const path = data?.path ?? data?.documentVersion?.path ?? defaultPath;
        const list = Array.isArray(data)
          ? data
          : [data?.records, data?.documentVersionResponses, data?.documents, data?.documentVersion?.documents].find(
              Array.isArray
            ) ?? [];
        const formatted = list.map((doc: any) => {
          const id = doc.documentId ?? doc.id;
          const blob = doc.blobUrl || (path ? `${path}/${id}` : "");
          return {
            ...doc,
            documentId: id,
            blobUrl: blob,
          };
        });
        setVersionFiles((all) => ({
          ...all,
          [key]: formatted,
        }));
      })
      .catch(() => undefined)
      .finally(() => {
        setLoadingVersionKeys((prev) => ({ ...prev, [key]: false }));
      });
  };

  const currentFiles = workOrderDocuments(detail);
  /* Newest first only on the default sort's first page. */
  /* The page's rows in the chosen order — also applied here, so what is shown
     always matches the column's arrow even if the endpoint ignores the sort. */
  const sortKey = (record: any) =>
    orderBy === "updatedOn"
      ? new Date(record.updatedOn ?? record.createdOn ?? 0).getTime()
      : parseDocVersionNumber(record.docVersion);
  const sortedRecords = [...records].sort((a, b) => (sortKey(a) - sortKey(b)) * (order === "asc" ? 1 : -1));
  const newest = page === 0 && order === "desc" && orderBy === "docVersion" ? sortedRecords[0]?.docVersion : undefined;
  /* Nothing listed yet: if draft status, show empty list (No data) matching SPA. */
  const rows = isDraftStatus
    ? []
    : records.length
    ? sortedRecords
    : [{ docVersion: 0, updatedOn: detail?.updatedOn ?? detail?.createdOn }];
  const overallPdfRegex = /^(SPA|WorkOrder).*\.pdf$/i;

  const versions = rows.map((record: any, index: number) => {
    const own = [
      versionFiles[String(record.docVersion)],
      record.documentVersionResponses,
      record.documents,
      record.documentResponses,
    ].find((value) => Array.isArray(value) && value.length);
    const files = own
      ? workOrderDocuments({ documentVersionResponses: own, envelopeId })
      : !records.length || String(record.docVersion) === String(newest)
      ? currentFiles
      : currentFiles.filter((doc) => String(doc.docVersion) === String(Number(record.docVersion)));
    
    const vLabel = versionLabel(record.docVersion);
    const cleanNo = String(detail?.workOrderNo ?? detail?.projectId ?? envelopeId ?? "document")
      .replace(/[\\/:*?"<>|]+/g, "-");
    const versionFileName =
      vLabel === "V0" || isFirstVersion(record.docVersion)
        ? `WorkOrder-${cleanNo}.pdf`
        : `WorkOrder-${cleanNo}(${vLabel}).pdf`;

    // In Work Order (similar to SPA), only the overall document is shown in Document Versions:
    const nonOverallFiles = files.filter(
      (f: any) => !overallPdfRegex.test(f.name ?? f.documentName ?? "")
    );
    const overallCandidate = files.find(
      (f: any) => overallPdfRegex.test(f.name ?? f.documentName ?? "")
    );

    const overallFile = {
      ...(overallCandidate || {}),
      name: versionFileName,
      key: overallCandidate?.key || `overall-${record.docVersion}-${index}`,
      url: overallCandidate?.url || "",
      generated: !overallCandidate?.url,
      files: nonOverallFiles,
      docVersion: record.docVersion,
    };

    return {
      key: `${record.docVersion}-${index}`,
      docVersion: record.docVersion,
      order: record.order ?? record.versionOrder,
      version: versionLabel(record.versionOrder ?? record.order ?? record.docVersion),
      lastUpdated: getFormattedDate(record.updatedOn ?? record.createdOn) || "-",
      files: [overallFile],
    };
  });
  const data = versions.filter((version) => {
    const s = search.trim().toLowerCase();
    if (!s) return true;
    return (
      version.version.toLowerCase().includes(s) ||
      String(version.docVersion ?? "").toLowerCase().includes(s)
    );
  });

  const generateAndUploadOverallPdf = async (
    targetEnvelopeId: string,
    versionKey: string,
    nextVersionOrder: number,
    draft: boolean,
    createId: number,
    allDocuments?: any[]
  ) => {
    try {
      const docDetail =
        allDocuments && allDocuments.length
          ? {
              ...detail,
              envelopeId: targetEnvelopeId,
              documentVersionResponses: allDocuments,
              documents: allDocuments,
              projectDocuments: allDocuments,
              documentVersion: undefined,
              docVersion: nextVersionOrder, versionHistory: [...records, { docVersion: nextVersionOrder, updatedOn: new Date().toISOString() }],
            }
          : { ...detail, docVersion: nextVersionOrder, versionHistory: [...records, { docVersion: nextVersionOrder, updatedOn: new Date().toISOString() }] };
      const pdfDoc = await workOrderPdf(docDetail);
      const blob: Blob = await new Promise((resolve, reject) => {
        try {
          pdfDoc.getBlob((b: Blob) => resolve(b));
        } catch (e) {
          reject(e);
        }
      });
      const cleanNo = String(detail?.workOrderNo ?? detail?.projectId ?? targetEnvelopeId ?? "document")
        .replace(/[\\/:*?"<>|]+/g, "-");
      const overallFileName = `WorkOrder-${cleanNo}(V${nextVersionOrder}).pdf`;
      const overallPdfFile = new File([blob], overallFileName, {
        type: "application/pdf",
        lastModified: Date.now(),
      });

      const overallFormData = new FormData();
      overallFormData.append("envelopeId", targetEnvelopeId);
      overallFormData.append("create-document-file", overallPdfFile);
      overallFormData.append("create_id", `${createId}`);
      overallFormData.append("versionOrder", `${nextVersionOrder}`);
      overallFormData.append("versionKey", versionKey);
      overallFormData.append("draft", `${draft}`);

      await workOrderApi.updatePanelDocument(overallFormData);
    } catch (err) {
      console.warn("Failed to generate or upload overall Work Order PDF:", err);
    }
  };

  const handleUploadOrUpdate = async ({
    files,
    deletedDocIds,
    remainingDocs,
    isFirstTime,
  }: EditDocumentSubmitPayload) => {
    if (!envelopeId) return;
    setIsUploading(true);
    const cleanEnvelopeId = envelopeId.replace(/-draft$/i, "");
    const statusIsDraft = String(detail?.status ?? "").toUpperCase() === "DRAFT";

    try {
      const versionKey = Math.floor(Math.random() * 1_000_000_000).toString();

      const getRecordMinorVersion = (record: any): number => {
        if (record?.versionOrder !== undefined && record?.versionOrder !== null) {
          const vo = Number(record.versionOrder);
          if (!isNaN(vo)) return vo;
        }
        if (record?.order !== undefined && record?.order !== null) {
          const o = Number(record.order);
          if (!isNaN(o)) return o;
        }
        const str = String(record?.docVersion ?? "");
        if (str.includes(".")) {
          const parts = str.split(".");
          const minor = parseInt(parts[1], 10);
          if (!isNaN(minor)) return minor;
        }
        const num = parseFloat(str);
        if (!isNaN(num)) {
          return Math.round(num);
        }
        return 0;
      };

      const maxExistingMinor = records.reduce((max: number, r: any) => {
        return Math.max(max, getRecordMinorVersion(r));
      }, 0);

      const nextVersionOrder = isFirstTime
        ? 1
        : Math.max(
            total > 0 ? total : 1,
            maxExistingMinor + 1
          );

      // 1. Call document-lock API (similar to SPA) - stop execution if it fails
      const lockRes: any = await workOrderApi.documentLock({
        envelopeId: cleanEnvelopeId,
        versionOrder: nextVersionOrder,
        versionKey,
        draft: statusIsDraft,
      });
      if (lockRes && lockRes.status && lockRes.status !== "OK" && lockRes.status !== "CREATED") {
        throw new Error(lockRes.message || "Document lock failed");
      }

      if (isFirstTime) {
        // At first time upload api need to be called
        const startDocId = nextDocumentId(detail);
        await workOrderApi.uploadDocuments({
          envelopeId: cleanEnvelopeId,
          files,
          firstDocumentId: startDocId,
          draft: statusIsDraft,
        });

        // Fetch updated documents after uploading (similar to SPA)
        let allDocuments: any[] = [];
        try {
          const updatedDocsRes: any = await workOrderApi
            .versionDocuments(cleanEnvelopeId, nextVersionOrder, statusIsDraft)
            .catch(() =>
              workOrderApi.currentDocuments(cleanEnvelopeId, statusIsDraft)
            );
          const rawDocs = Array.isArray(updatedDocsRes)
            ? updatedDocsRes
            : [
                updatedDocsRes?.records,
                updatedDocsRes?.data,
                updatedDocsRes?.documentVersionResponses,
                updatedDocsRes?.documents,
                updatedDocsRes?.response?.data,
              ].find(Array.isArray) ?? [];
          allDocuments = rawDocs.filter(
            (d: any) => !overallPdfRegex.test(d?.documentName ?? d?.name ?? "")
          );
        } catch (e) {
          console.warn("Could not fetch updated documents:", e);
        }

        const existingFirst = (detail?.documentVersionResponses ?? detail?.documents ?? []).filter(
          (d: any) => !overallPdfRegex.test(d?.documentName ?? d?.name ?? "")
        );
        existingFirst.forEach((doc: any) => {
          const docId = String(doc.documentId ?? doc.id ?? doc.key);
          const exists = allDocuments.some(
            (d: any) =>
              String(d.documentId ?? d.id ?? d.key) === docId ||
              (d.name ?? d.documentName) === (doc.name ?? doc.documentName)
          );
          if (!exists) {
            allDocuments.push(doc);
          }
        });

        if (files && files.length > 0) {
          files.forEach((file, idx) => {
            const exists = allDocuments.some(
              (d: any) => (d.name ?? d.documentName) === file.name
            );
            if (!exists) {
              allDocuments.push({
                documentId: String(startDocId + idx),
                documentName: file.name,
                name: file.name,
              });
            }
          });
        }

        const nextOverallCreateId = Math.max(
          startDocId + files.length,
          ...(allDocuments.length
            ? allDocuments.map((d: any) => (parseInt(String(d.documentId ?? d.id ?? d.key), 10) || 0) + 1)
            : [startDocId + files.length])
        );

        // Generate and upload overall Work Order PDF
        await generateAndUploadOverallPdf(
          cleanEnvelopeId,
          versionKey,
          nextVersionOrder,
          statusIsDraft,
          nextOverallCreateId,
          allDocuments
        );

        // Call trigger document-version-mail API (similar to SPA)
        await workOrderApi.triggerDocumentVersionMail({
          envelopeId: cleanEnvelopeId,
          versionOrder: nextVersionOrder,
          draft: statusIsDraft,
        });

        openNotification({
          title: "Documents uploaded",
          message: `${files.length} document${files.length > 1 ? "s" : ""} uploaded successfully.`,
          type: "success",
        });
      } else {
        // Subsequent times: delete and update apis similar to SPA with overall document
        const allExistingDocs = (detail?.documentVersionResponses ?? detail?.documents ?? []).concat(
          records.flatMap((r: any) => r.documents ?? r.documentVersionResponses ?? [])
        );
        const existingOverallDoc = allExistingDocs.find(
          (d: any) => overallPdfRegex.test(d?.documentName ?? d?.name ?? "")
        );
        const allDeleteIds = [...deletedDocIds];
        if (existingOverallDoc) {
          const overallId = String(existingOverallDoc.documentId ?? existingOverallDoc.id);
          if (overallId && !allDeleteIds.includes(overallId)) {
            allDeleteIds.push(overallId);
          }
        }

        // Call delete API (/document/delete/PanelDocument)
        const deleteFormData = new FormData();
        deleteFormData.append("envelopeId", cleanEnvelopeId);
        deleteFormData.append("delete_id", allDeleteIds.join(","));
        const actualRemainingDocs = remainingDocs.filter(
          (d) => !allDeleteIds.includes(String(d.documentId ?? d.key))
        );
        deleteFormData.append(
          "existing_id",
          actualRemainingDocs.map((d) => String(d.documentId ?? d.key)).join(",")
        );
        deleteFormData.append(
          "existing_file_names",
          actualRemainingDocs.map((d) => encodeURIComponent(d.name)).join(",")
        );
        deleteFormData.append("versionOrder", `${nextVersionOrder}`);
        deleteFormData.append("versionKey", versionKey);
        deleteFormData.append("draft", `${statusIsDraft}`);

        await workOrderApi.deletePanelDocument(deleteFormData);

        // Call update API (/document/update/PanelDocument) for each new file
        const maxExistingDocId = Math.max(
          99,
          ...savedDocuments.map((d) => parseInt(String(d.documentId ?? d.key), 10) || 0)
        );
        let currentCreateId = maxExistingDocId;

        if (files && files.length > 0) {
          for (const file of files) {
            currentCreateId += 1;
            const createFormData = new FormData();
            createFormData.append("envelopeId", cleanEnvelopeId);
            createFormData.append("create-document-file", file);
            createFormData.append("create_id", `${currentCreateId}`);
            createFormData.append("versionOrder", `${nextVersionOrder}`);
            createFormData.append("versionKey", versionKey);
            createFormData.append("draft", `${statusIsDraft}`);

            await workOrderApi.updatePanelDocument(createFormData);
          }
        }

        // Fetch updated documents after uploading (similar to SPA)
        let allDocuments: any[] = [];
        try {
          const updatedDocsRes: any = await workOrderApi
            .versionDocuments(cleanEnvelopeId, nextVersionOrder, statusIsDraft)
            .catch(() =>
              workOrderApi.currentDocuments(cleanEnvelopeId, statusIsDraft)
            );
          const rawDocs = Array.isArray(updatedDocsRes)
            ? updatedDocsRes
            : [
                updatedDocsRes?.records,
                updatedDocsRes?.data,
                updatedDocsRes?.documentVersionResponses,
                updatedDocsRes?.documents,
                updatedDocsRes?.response?.data,
              ].find(Array.isArray) ?? [];
          allDocuments = rawDocs
            .filter((d: any) => !overallPdfRegex.test(d?.documentName ?? d?.name ?? ""))
            .filter((d: any) => !allDeleteIds.includes(String(d?.documentId ?? d?.id ?? d?.key)));
        } catch (e) {
          console.warn("Could not fetch updated documents:", e);
        }

        actualRemainingDocs.forEach((doc: any) => {
          const docId = String(doc.documentId ?? doc.key ?? doc.id);
          const exists = allDocuments.some(
            (d: any) =>
              String(d.documentId ?? d.id ?? d.key) === docId ||
              (d.name ?? d.documentName) === doc.name
          );
          if (!exists) {
            allDocuments.push({
              ...doc,
              documentId: docId,
              documentName: doc.name ?? doc.documentName,
            });
          }
        });

        if (files && files.length > 0) {
          files.forEach((file, idx) => {
            const exists = allDocuments.some(
              (d: any) => (d.name ?? d.documentName) === file.name
            );
            if (!exists) {
              allDocuments.push({
                documentId: String(maxExistingDocId + idx + 1),
                documentName: file.name,
                name: file.name,
              });
            }
          });
        }

        const nextOverallCreateId = Math.max(
          currentCreateId + 1,
          ...(allDocuments.length
            ? allDocuments.map((d: any) => (parseInt(String(d.documentId ?? d.id ?? d.key), 10) || 0) + 1)
            : [currentCreateId + 1])
        );

        // Generate and upload overall Work Order PDF
        await generateAndUploadOverallPdf(
          cleanEnvelopeId,
          versionKey,
          nextVersionOrder,
          statusIsDraft,
          nextOverallCreateId,
          allDocuments
        );

        // Call trigger document-version-mail API (similar to SPA)
        await workOrderApi.triggerDocumentVersionMail({
          envelopeId: cleanEnvelopeId,
          versionOrder: nextVersionOrder,
          draft: statusIsDraft,
        });

        openNotification({
          title: "Document Version",
          message: "You have successfully added new version of document",
          type: "success",
        });
      }

      setModalOpen(false);
      onUploaded?.();
    } catch (error: any) {
      const errorMsg =
        (typeof error === "string" ? error : null) ||
        error?.response?.data?.message ||
        error?.response?.data?.response?.message ||
        (typeof error?.response?.data?.response?.data === "string"
          ? error.response.data.response.data
          : null) ||
        error?.data?.message ||
        error?.message ||
        "Document lock failed";
      openNotification({
        title: errorMsg,
        message: "",
        type: "error",
      });
    } finally {
      setIsUploading(false);
    }
  };

  /* Sorted by the server: the column's field as the versions endpoint names it.
     Each click flips ascending / descending — the default cycle's third,
     "unsorted", click used to fall back to descending, so a descending column
     never changed. */
  const SORT_FIELD: Record<string, string> = { version: "docVersion", lastUpdated: "updatedOn" };
  const sortOrderOf = (field: string) =>
    orderBy === SORT_FIELD[field] ? (order === "asc" ? "ascend" : "descend") : undefined;
  /* The documents the work order holds now, for the Edit Document modal: the
     current-version list, or — when that answers with nothing — the newest
     version's own files. */
  const newestVersion = records.reduce(
    (best: any, record: any) =>
      best === undefined || parseDocVersionNumber(record.docVersion) > parseDocVersionNumber(best)
        ? record.docVersion
        : best,
    undefined
  );
  const savedDocuments: SavedDocument[] = useMemo(() => {
    return (
      currentFiles.length
        ? currentFiles
        : workOrderDocuments({ documentVersionResponses: versionFiles[String(newestVersion)] ?? [] })
    )
      .filter((doc: any) => !overallPdfRegex.test(doc.name ?? doc.documentName ?? ""))
      .map((doc: any) => ({
        key: String(doc.id ?? doc.key),
        documentId: String(doc.id ?? doc.key),
        name: doc.name,
        url: doc.url,
      }));
  }, [currentFiles, versionFiles, newestVersion]);
  const openEdit = () => {
    setIsReturningFromPreview(false);
    if (!isDraftStatus && !currentFiles.length && newestVersion !== undefined && !isFirstVersion(newestVersion)) {
      loadVersion(newestVersion);
    }
    setModalOpen(true);
  };

  const columns: TableColumnsType<any> = [
    { title: "Version", dataIndex: "version", sorter: true, sortOrder: sortOrderOf("version"), sortDirections: FLIP },
    {
      title: "Last Updated",
      dataIndex: "lastUpdated",
      sorter: true,
      sortOrder: sortOrderOf("lastUpdated"),
      sortDirections: FLIP,
    },
  ];

  const view = (file: any) => {
    const isOverall = overallPdfRegex.test(file.name);
    const versionDocs = (file.files && file.files.length ? file.files : currentFiles)
      ?.filter((f: any) => !overallPdfRegex.test(f.name ?? f.documentName ?? ""));

    if (isOverall && workOrderPdf) {
      const docDetail = versionDocs && versionDocs.length
        ? { ...detail, documents: versionDocs, documentVersionResponses: versionDocs, projectDocuments: versionDocs, documentVersion: undefined, docVersion: file.docVersion, versionHistory: records }
        : { ...detail, docVersion: file.docVersion, versionHistory: records };
      return workOrderPdf(docDetail).then((doc) =>
        doc.getBlob((blob) => setPreview({ name: file.name, url: URL.createObjectURL(blob), revoke: true }))
      );
    }

    if (file.generated) {
      return workOrderPdf({ ...detail, docVersion: file.docVersion, versionHistory: records }).then((doc) =>
        doc.getBlob((blob) => setPreview({ name: file.name, url: URL.createObjectURL(blob), revoke: true }))
      );
    }
    return setPreview({ name: file.name, url: file.url });
  };

  const download = (file: any) => {
    const isOverall = overallPdfRegex.test(file.name);
    const versionDocs = (file.files && file.files.length ? file.files : currentFiles)
      ?.filter((f: any) => !overallPdfRegex.test(f.name ?? f.documentName ?? ""));

    if (isOverall && workOrderPdf) {
      const docDetail = versionDocs && versionDocs.length
        ? { ...detail, documents: versionDocs, documentVersionResponses: versionDocs, projectDocuments: versionDocs, documentVersion: undefined, docVersion: file.docVersion, versionHistory: records }
        : { ...detail, docVersion: file.docVersion, versionHistory: records };
      return workOrderPdf(docDetail).then((doc) => doc.download(file.name));
    }

    if (file.generated) return workOrderPdf({ ...detail, docVersion: file.docVersion, versionHistory: records }).then((doc) => doc.download(file.name));
    return downloadDocument(file.url, file.name);
  };

  const handleViewFromEdit = (document: SavedDocument | File) => {
    setPreviewFromEdit(true);
    setIsReturningFromPreview(true);
    setModalOpen(false);
    if (document instanceof File) {
      setPreview({ name: document.name, url: URL.createObjectURL(document), revoke: true });
    } else {
      view(document);
    }
  };

  const closePreview = () => {
    if (preview?.revoke) URL.revokeObjectURL(preview.url);
    setPreview(null);
    if (previewFromEdit) {
      setPreviewFromEdit(false);
      setModalOpen(true);
      setTimeout(() => setIsReturningFromPreview(false), 200);
    }
  };

  return (
    <>
      <Row
        justify="space-between"
        align="middle"
        style={{ marginBottom: 16 }}
      >
        <Col
          xs={24}
          sm={10}
        >
          <Input
            placeholder="Search Version"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            prefix={
              <Icon
                icon="ic:outline-search"
                className={styles.viewIcon}
              />
            }
          />
        </Col>
        {/* Approvers review the documents from Work Order Approval, and a draft
            is edited in the wizard; adding a version is for a submitted work
            order's own page. */}
        {!hasKeyString("approval") && !isDraftStatus && !isCompleted && (
          <Col>
            <Button
              type="primary"
              onClick={openEdit}
            >
              <Space>
                <Icon
                  icon="akar-icons:plus"
                  inline
                />
                Add
              </Space>
            </Button>
          </Col>
        )}
      </Row>

      <Table
        columns={columns}
        dataSource={data}
        loading={isLoading}
        pagination={false}
        rowKey="key"
        onChange={(_, __, sorter: any) => {
          setOrderBy(SORT_FIELD[sorter.field] ?? "docVersion");
          setOrder(sorter.order === "ascend" ? "asc" : "desc");
          setPage(0);
        }}
        expandable={{
          onExpand: (expanded, record: any) => {
            const isVer0 =
              String(record?.docVersion) === "0" ||
              String(record?.docVersion) === "1.0" ||
              record?.order === 0 ||
              isFirstVersion(record?.docVersion);
            if (!isDraftStatus && !isVer0 && expanded) {
              loadVersion(record.docVersion, record.order);
            }
          },
          expandedRowRender: (record: any) => {
            const isLoadingThis = loadingVersionKeys[String(record.docVersion)];
            if (isLoadingThis) {
              return (
                <div style={{ padding: "12px 16px" }}>
                  <Spin size="small" />
                </div>
              );
            }
            if (!record.files.length) {
              return <Typography.Text type="secondary">No documents in this version</Typography.Text>;
            }
            return record.files.map((file: any) => (
              <div
                className={styles.fileRow}
                key={file.key}
              >
                <Icon
                  icon="vscode-icons:file-type-pdf2"
                  className={styles.fileIcon}
                />
                <Typography.Text>{file.name}</Typography.Text>
                <Icon
                  icon="akar-icons:eye"
                  role="button"
                  aria-label={`View ${file.name}`}
                  className={styles.viewIcon}
                  onClick={() => view(file)}
                />
                <Icon
                  icon="mdi:tray-arrow-down"
                  role="button"
                  aria-label={`Download ${file.name}`}
                  className={styles.viewIcon}
                  onClick={() => download(file)}
                />
              </div>
            ));
          },
        }}
      />

      <div className="pagination">
        <Pagination
          current={page + 1}
          total={total}
          pageSize={PAGE_SIZE}
          showSizeChanger={false}
          onChange={(next) => setPage(next - 1)}
        />
      </div>

      <EditDocumentModal
        open={modalOpen}
        onCancel={() => {
          setIsReturningFromPreview(false);
          setModalOpen(false);
        }}
        onSubmit={handleUploadOrUpdate}
        isSubmitting={isUploading}
        saved={savedDocuments}
        onView={handleViewFromEdit}
        isReturningFromPreview={isReturningFromPreview}
      />

      <PDFViewer
        show={!!preview}
        setShow={closePreview}
        title={preview?.name ?? ""}
        url={preview?.url ?? ""}
      />
    </>
  );
};
